import { stat, type RuntimeObject, type RuntimePlayer, type RuntimeTimer } from "./state";
import { cardArtUrl, cardName } from "./cards";
import { TENT } from "../links";

/**
 * THE IN-MATCH HUD. The furniture a player reads the game from.
 *
 * 🚨 **BUILT FROM THE DENOMINATOR, NOT FROM WHAT I HAPPENED TO NOTICE.** Alex, 2026-08-14:
 * *"there is no menu of players names, points, firefox amount, attack feature or animations, the
 * pool to sacrifice cards for firefox, the 'home shield' or 'home character' that you are trying to
 * kill to win the game... so I am not whack-a-moling this."*
 *
 * The list is `.claude/skills/revival-match-board/SKILL.md` §1, taken from the original's own
 * `GameView` and `*View` classes. Each piece below names which entry it is.
 *
 * ⚠️ **NONE OF THIS DECIDES ANYTHING.** Every number comes from the server's state. The clock counts
 * down from the timer's `EndTime` for DISPLAY only - the server owns expiry, and a client that
 * decided a turn was over would be deciding a rule.
 */

/**
 * §1.1 THE HERO - the home character you are trying to kill.
 *
 * ⭐ **THIS IS THE WIN CONDITION MADE VISIBLE**, and the board had it as a number in a text line.
 * Portrait, name, HP. `HeroHolderView` / `HeroView` in the original.
 *
 * ⚠️ The hero is a `RuntimeObject` of `Type: "Hero"` - its `DataId` resolves to a hero record whose
 * `artUrl` is already staged alongside the cards.
 */
export function heroEl(
  hero: RuntimeObject | undefined,
  player: RuntimePlayer | undefined,
  opts: { targetable?: boolean; onTarget?: () => void } = {},
): HTMLElement {
  const root = document.createElement(opts.targetable ? "button" : "div");
  // ⭐ **THE SHIELD, AS THE ORIGINAL DRAWS IT** (`art/cdn/boardlayout.png`, item 6): a HEXAGON at the
  // centre of the edge with the portrait inside and the HP as an orb on it. Alex asked *"where is
  // the shield or the 'home' that the player is supposed to attack"* while looking at a small
  // portrait card off to one side - because that is not what a shield looks like.
  root.className = "shield";
  // ⭐ **THE HERO NEEDS `data-obj` TOO, OR THE ONE HIT THAT DECIDES THE MATCH IS INVISIBLE.**
  // Combat animations are looked up by runtime id; only cards carried the attribute, so striking a
  // HERO - the win condition - produced no shake, no damage number and no sound. Found by a gate
  // that attacked the hero rather than a creature.
  if (typeof hero?.Id === "number") root.dataset["obj"] = String(hero.Id);
  if (opts.targetable) {
    (root as HTMLButtonElement).type = "button";
    root.dataset["label"] = "ATTACK HERO";
    root.classList.add("shield--target");
    if (opts.onTarget) root.addEventListener("click", opts.onTarget);
  }

  // ⭐ **OUR BONE SHIELD, NOT A HEXAGON.** The portrait sits INSIDE the frame's opening and the HP
  // reads in its orb - the arrangement of `art-src/board-shield/BorderNew_new.png`. Alex: *"no
  // hexagons please that is the original game design. What about our bone Shell?"*
  const frame = document.createElement("span");
  frame.className = "shield__frame";
  frame.setAttribute("aria-hidden", "true");

  const art = document.createElement("div");
  art.className = "shield__art";
  const url = hero?.DataId ? cardArtUrl(hero.DataId) : undefined;
  if (url) art.style.backgroundImage = `url("/art/cards/${url}.webp")`;

  const name = document.createElement("span");
  name.className = "shield__name";
  // ⚠️ textContent. A player's name is another person's text.
  name.textContent = player?.UserName ?? cardName(hero?.DataId);

  const hp = document.createElement("span");
  hp.className = "shield__hp";
  hp.textContent = String(stat(hero?.Hp));

  root.append(art, frame, name, hp);
  return root;
}

/**
 * §1.3 THE FOXFIRE POOL, as PIPS.
 *
 * ⚠️ **A NUMBER IS NOT A RESOURCE BAR.** The original draws `LavaItemView` per point so a player
 * reads their pool at a glance mid-turn without doing arithmetic. Spent pips stay visible as empties
 * for the same reason: how much you HAD is information.
 */
export function foxfirePips(player: RuntimePlayer | undefined): HTMLElement {
  const rail = document.createElement("div");
  rail.className = "foxfire";

  const have = stat(player?.Mana);
  const max = player?.Mana?.Max ?? 0;

  // ⭐ **`n/max` THEN TEN DIAMONDS**, exactly as the original's board diagram shows it (item 5).
  // The COUNT tells you what you have this turn; the TEN tell you where you are in the ramp, which
  // is the shape of the whole game. Drawing only `max` diamonds hides that.
  // ⭐ **THE WORD, THEN THE NUMBER.** Alex, 2026-08-14: *"Where does it show my Foxfire amount?"* -
  // asked while `0/4` was on screen beside his hero. An unlabelled ratio next to a portrait is not
  // an answer; the player has to already know what it counts. The resource the whole game is
  // spent in gets its name written next to it.
  const label = document.createElement("span");
  label.className = "foxfire__label";
  label.textContent = "FOXFIRE";
  rail.append(label);

  const n = document.createElement("span");
  n.className = "foxfire__count";
  n.textContent = `${have}/${max}`;
  // ⚠️ The tooltip states the RULE, because the number alone cannot: foxfire refills every turn and
  // the ceiling grows - which is why "why did my foxfire reset" is otherwise unanswerable in-game.
  n.title = `${have} foxfire to spend this turn, out of ${max}. It refills at the start of every `
    + "turn and the ceiling grows as the match goes on. Burning a card adds that card's cost.";
  rail.append(n);

  const pips = document.createElement("span");
  pips.className = "foxfire__pips";
  for (let i = 0; i < 10; i++) {
    const pip = document.createElement("span");
    pip.className =
      i < have ? "pip pip--lit" : i < max ? "pip pip--available" : "pip pip--locked";
    pips.append(pip);
  }
  rail.append(pips);
  return rail;
}

/**
 * §1.4 THE TURN CLOCK.
 *
 * ⚠️ **DISPLAY ONLY.** `inGameTimerSec` is 61 and the SERVER expires a turn - `MatchSession`'s own
 * ticker does it, because `RuntimeTimer` has no tick and nothing in the engine notices `EndTime`
 * passing. A client that acted on reaching zero would be deciding a rule it does not own.
 *
 * ⚠️ Returns null when there is no deadline (`EndTime` is `0001-01-01` before the match starts), so
 * the caller can render nothing rather than "-1".
 */
export function secondsLeft(timer: RuntimeTimer): number | null {
  const raw = timer.EndTime;
  if (!raw || raw.startsWith("0001")) return null;
  const end = Date.parse(raw.endsWith("Z") ? raw : raw + "Z");
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.round((end - Date.now()) / 1000));
}

export function clockEl(timer: RuntimeTimer, ourTurn: boolean): HTMLElement {
  const el = document.createElement("div");
  el.className = ourTurn ? "clock clock--ours" : "clock";
  const s = secondsLeft(timer);

  /* 🚨 **IT WAS A BARE NUMBER FLOATING BESIDE THE BOARD.** In Alex's own screenshots it reads as
   * "30" or "51" halfway down the right edge with nothing to say what it counts - so the one control
   * that tells a player how long they have to act did not announce itself as a clock at all. He then
   * described the rule back to us as if it were missing: *"there is a 45 or 1 min counter for the
   * player to click end turn"* - which is exactly what the game already does, invisibly.
   *
   * ⭐ **A NUMBER NEEDS A NOUN.** The label says whose clock it is, so the countdown is legible as a
   * deadline on the player rather than as decoration. */
  /* 🔴 **THE LABEL SAID "YOUR TURN", WHICH IS WHAT THE BANNER ALREADY SHOUTS AT THE TOP OF THE
   * BOARD.** So the clock was a number under a duplicate of another control's words, and Alex looked
   * straight at a live countdown and said *"still no timer"* - twice. A label that repeats a
   * different element does not identify this one; it makes it look like part of that one.
   *
   * ⭐ It now says what the number MEANS, and the number carries its unit. "TIME LEFT / 47s" cannot
   * be read as anything except a clock. */
  const label = document.createElement("span");
  label.className = "clock__label";
  label.textContent = "TIME LEFT";

  const value = document.createElement("span");
  value.className = "clock__value";
  value.textContent = s === null ? "" : `${s}s`;

  el.append(label, value);
  // ⚠️ The last ten seconds are the only time this needs to shout.
  if (s !== null && s <= 10) el.classList.add("clock--urgent");
  return el;
}

/**
 * §1.5 / §1.6 THE DECK AND GRAVEYARD PILES.
 *
 * ⚠️ **A COUNT IN A SENTENCE IS NOT A PILE.** A player tracks "how much deck is left" spatially; the
 * original draws `DeckHolderView` and `GraveyardPileView` as objects on the board with a counter on
 * them, and deck-out is a real losing condition to watch.
 */
export function pileEl(
  kind: "deck" | "graveyard",
  count: number,
  onOpen?: () => void,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "pilewrap";

  // ⭐ **THE GRAVEYARD OPENS. `GraveyardView` in the original is a real screen, not a counter.**
  // Alex, 2026-08-14: *"we should be able to look at the cards that are in the graveyard and see
  // them clearly."* It matters mechanically as well as for reading the game - several cards
  // resurrect or count what is in there ("gains +1/+0 for each X card in your graveyard"), so a
  // player who cannot look is playing with hidden information that is supposed to be public.
  const el = document.createElement(onOpen ? "button" : "div");
  if (onOpen) {
    (el as HTMLButtonElement).type = "button";
    el.dataset["label"] = "GRAVEYARD";
    el.addEventListener("click", onOpen);
    el.classList.add("pile--openable");
  }
  el.classList.add("pile", `pile--${kind}`);
  el.title = kind === "deck" ? "cards left in your deck" : "cards in the graveyard";

  const n = document.createElement("span");
  n.className = "pile__count";
  n.textContent = String(count);
  el.append(n);

  // An empty pile is drawn as an empty SLOT, not as nothing - a vanished deck reads as a bug.
  if (count === 0) el.classList.add("pile--empty");

  // 🚨 **LABELLED, BECAUSE AN UNLABELLED PILE IS AN UNFINDABLE ONE.** Alex, 2026-08-14, looking
  // straight at a board that HAD one: *"where is the graveyard?"* It was drawn - two small card
  // backs in a corner with a number on them. Present is not the same as findable, and on a busy
  // board a player should never have to work out what a shape is.
  const label = document.createElement("span");
  label.className = "pilewrap__label";
  label.textContent = kind === "deck" ? "DECK" : "GRAVEYARD";

  wrap.append(el, label);
  return wrap;
}

/**
 * §1.17 THE FOXFIRE BASIN - the place you feed a card to.
 *
 * 🚨 **A BURN BUTTON ON A CARD IS NOT A POOL.** Alex, 2026-08-14: *"where is the pool to sacrifice
 * your cards for foxfire?"* The mechanic is Day Devil's and it is spatial - you give a card TO
 * something. Rendering it as a small control on the card made the game's own new mechanic invisible,
 * and it is the one thing about Revival that is ours.
 *
 * ⚠️ It is a real DROP TARGET as well as a click target, so the gesture matches the idea.
 *
 * 🚨 **AND IT USED TO DESTROY THE CARD THE INSTANT YOU LET GO.** DayDevil, beta feedback
 * 2026-08-20: *"position of foxfire burn is also dangerously close to where to place minions, would
 * maybe put on the side, easy to activate it by mistake."*
 *
 * ⛔ **HE IS DESCRIBING A MISFIRE ON AN IRREVERSIBLE ACTION, WHICH IS THE WORST KIND.** The basin
 * sits at the centre of the ring, immediately above your own row - the board is a flex column of
 * `opponent row · centre (the basin) · our row`, so the basin is directly between the hand and the
 * slots the hand is aimed at. A drag that overshoots the row lands in the fire, and `sacrifice`
 * called `POST /Revival/Foxfire/Sacrifice` with no confirmation and no undo. The card is gone.
 *
 * ⭐ **THE FIX IS A SECOND DELIBERATE ACT, NOT A NEW LOCATION.** Moving the basin to the rail was
 * the obvious response and it is the wrong one: the centre placement is deliberate (*"the mechanic
 * is ours and it is spatial - you give a card TO the fire"*), and a control you can hit by accident
 * at the edge of the board is still a control you can hit by accident. Dropping now ARMS the basin
 * and names the card; a second click commits and ESC or a click anywhere else returns it. That is
 * the same shape as `aiming` in `screens/match.ts`, which is already how this game asks "are you
 * sure" about a target.
 */
export function basinEl(
  onBurn: (objectId: number) => void,
  describe?: (objectId: number) => string | undefined,
): HTMLElement {
  const el = document.createElement("div");
  el.className = "basin";
  el.dataset["label"] = "FOXFIRE BASIN";

  const flame = document.createElement("span");
  flame.className = "basin__flame";
  flame.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.className = "basin__label";
  // The basin is the mechanic's own surface, so it is where the rule belongs. "FEED THE FOXFIRE"
  // named the gesture and taught nothing; a player still had to discover that the token they get is
  // the colour of the card they gave.
  label.textContent = "FEED THE FOXFIRE";
  const rule = document.createElement("span");
  rule.className = "basin__rule";
  rule.textContent = "drop a card here for 1 token of its realm";
  el.dataset["rule"] = "1 token of the card's own realm";

  el.append(flame, label, rule);

  /** The card waiting for a second confirmation. `null` means the basin is idle. */
  let armed: number | null = null;

  function disarm(): void {
    armed = null;
    el.classList.remove("basin--armed");
    label.textContent = "FEED THE FOXFIRE";
    rule.textContent = "drop a card here for 1 token of its realm";
  }

  function arm(id: number): void {
    armed = id;
    el.classList.add("basin--armed");
    const what = describe?.(id);
    // ⚠️ **NAME THE CARD.** "Confirm?" on a board with seven cards on it is not a question the
    // player can answer; the whole risk being guarded against is that they dropped the WRONG one.
    label.textContent = what ? `BURN ${what.toUpperCase()}?` : "BURN THIS CARD?";
    rule.textContent = "click to confirm  ·  Esc to keep it";
  }

  el.addEventListener("dragover", (e) => {
    e.preventDefault();
    el.classList.add("basin--over");
  });
  el.addEventListener("dragleave", () => el.classList.remove("basin--over"));
  el.addEventListener("drop", (e) => {
    e.preventDefault();
    el.classList.remove("basin--over");
    const raw = e.dataTransfer?.getData("text/plain");
    const id = Number(raw);
    // ⚠️ A non-numeric payload is somebody else's drag, not ours. Ignore it rather than sending 0.
    if (Number.isFinite(id) && id > 0) arm(id);
  });

  // ⭐ The commit. A click on an IDLE basin does nothing, so the basin can never burn a card the
  // player did not first drag onto it.
  el.addEventListener("click", () => {
    if (armed === null) return;
    const id = armed;
    disarm();
    onBurn(id);
  });

  // ⛔ **ANY OTHER CLICK CANCELS**, so walking away from the decision keeps the card. Registered in
  // the CAPTURE phase on the document so it runs before the board's own handlers, and it checks
  // containment so the confirming click above is not cancelled by itself.
  document.addEventListener("click", (e) => {
    if (armed !== null && !el.contains(e.target as Node)) disarm();
  }, true);
  document.addEventListener("keydown", (e) => {
    if (armed !== null && e.key === "Escape") { e.preventDefault(); disarm(); }
  });

  return el;
}

/**
 * §1.9 THE OPPONENT'S HAND, face down.
 *
 * ⚠️ **HOW MANY CARDS THEY HOLD IS PUBLIC INFORMATION AND IT MATTERS.** It is how a player judges
 * whether to commit to the board. Drawn as backs so the count is read at a glance.
 */
export function opponentHandEl(count: number): HTMLElement {
  const el = document.createElement("div");
  el.className = "ohand";
  for (let i = 0; i < count; i++) {
    const back = document.createElement("span");
    back.className = "ohand__back";
    el.append(back);
  }
  return el;
}

/**
 * §1.11 THE YOUR-TURN OVERLAY - the most-seen surface in the game.
 *
 * ⚠️ It announces and LEAVES. A banner that stays is a banner over the board.
 */
export function announce(host: HTMLElement, text: string, side?: "ours" | "theirs"): void {
  const el = document.createElement("div");
  // ⭐ The side drives the colour, so "whose turn" is answered before the words are read.
  el.className = "announce" + (side ? ` announce--${side}` : "");
  el.textContent = text;
  host.append(el);
  // ⚠️ Removed on animation end rather than on a timer, so the two can never disagree.
  el.addEventListener("animationend", () => el.remove());
}


/**
 * §1.6 THE GRAVEYARD, OPEN. Every card in it, at a size you can actually read.
 *
 * ⚠️ **PUBLIC INFORMATION, SO IT IS SHOWN IN FULL.** Cards count what is in a graveyard and cards
 * resurrect out of it, so hiding it turns a public zone into hidden information and quietly changes
 * the game. Both players' graveyards are readable for the same reason.
 *
 * ⚠️ Closes on ESCAPE and on the scrim, not only on a button - a modal you can only leave one way is
 * a modal players get stuck in.
 */
export function graveyardOverlay(
  title: string,
  cards: RuntimeObject[],
  renderCard: (c: RuntimeObject) => HTMLElement,
): HTMLElement {
  const scrim = document.createElement("div");
  scrim.className = "overlay";

  const panel = document.createElement("div");
  panel.className = "overlay__panel";

  const head = document.createElement("h2");
  head.className = "overlay__title";
  head.textContent = `${title} — ${cards.length}`;

  const close = document.createElement("button");
  close.type = "button";
  close.className = "utilbtn overlay__close";
  close.dataset["label"] = "CLOSE";
  close.textContent = "CLOSE";

  const grid = document.createElement("div");
  grid.className = "overlay__grid";
  if (cards.length === 0) {
    const empty = document.createElement("p");
    empty.className = "board__empty";
    empty.textContent = "nothing here yet";
    grid.append(empty);
  } else {
    for (const c of cards) grid.append(renderCard(c));
  }

  const dismiss = () => scrim.remove();
  close.addEventListener("click", dismiss);
  scrim.addEventListener("click", (e) => {
    if (e.target === scrim) dismiss();
  });
  scrim.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Escape") dismiss();
  });
  scrim.tabIndex = -1;

  panel.append(head, grid, close);
  scrim.append(panel);
  // Focus so ESCAPE reaches it without the player clicking first.
  queueMicrotask(() => scrim.focus());
  return scrim;
}


/**
 * THE END OF THE MATCH.
 *
 * ⭐ **A MATCH HAS TO FINISH, NOT JUST STOP.** Until now a finished board changed one line of text
 * and swapped a button - so the most important moment in the game was the least visible thing on
 * the screen. This is the moment: it takes the board, names the result, says HOW it ended, and
 * offers the two things a player wants next.
 *
 * ⚠️ **THE RESULT IS THE ENGINE'S, NEVER INFERRED FROM HP.** A concession, a timeout and a
 * disconnect all end a match with both heroes standing.
 *
 * ⚠️ **IT DOES NOT TRAP.** `PLAY AGAIN` and `LEAVE` are both here, and Escape leaves - an overlay
 * over a finished game with one exit is a dead end wearing a celebration.
 */
/**
 * Show the newest award receipt, if this win paid one.
 *
 * ⚠️ **NEVER THROWS AND NEVER BLOCKS.** The receipt is a courtesy on top of a reward the player
 * already holds; a failure here must leave the victory card exactly as it was.
 *
 * ⚠️ **"NEWEST" IS TAKEN FROM THE SERVER'S ORDER, NOT FROM A TIMESTAMP COMPARISON HERE.** The route
 * returns receipts newest-first and it is the one owner of that order.
 */
async function showPrize(host: HTMLElement): Promise<void> {
  try {
    const { Api } = await import("../net/api");
    const { cardName, loadCardNames } = await import("./cards");
    const res = await Api.awards();
    const all = res?.awards ?? [];

    // 🚨 **ONE WIN NOW PAYS TWO RECEIPTS, AND SHOWING `awards[0]` WOULD HIDE ONE OF THEM.**
    //
    // Since 2026-08-16 a campaign win pays the campaign ladder AND the capped Longhunter drop, from
    // two different collections with two different rules. This function used to take the newest
    // receipt and stop, so whichever landed second would be the only one a player ever saw - and the
    // Longhunter card is the scarce one, the whole point of the beta. Alex's complaint that started
    // all of this was *"I have won 2 campaigns already and have no idea what I won"*; silently
    // dropping half of a reward is that same defect with a smaller number.
    //
    // ⚠️ **BOUNDED BY TIME, because the ledger is a HISTORY.** Receipts persist, so without a window
    // the victory screen would re-celebrate a card won yesterday every time you win anything. 90
    // seconds is generous against the end-of-match animation and far short of another match.
    const fresh = all.filter((a) => {
      const t = Date.parse(a.atUtc || "");
      return Number.isFinite(t) && Date.now() - t < 90_000;
    }).slice(0, 2);
    /* 🔴 **THIS WAS `fresh.length ? fresh : all.slice(0, 1)`, AND THE FALLBACK UNDID THE WINDOW.**
       Alex, 2026-08-18, on a DEFEAT screen: *"why am i getting a card? i lost, and it is not my
       first game."* He was shown "LONGHUNTER FIND - Hide of the Canyon Bull, Talons of the Cliff
       Hag - It is yours to keep", on a match that paid him nothing. Measured on the live host: his
       only Longhunter receipts are dated **2026-08-16 and 2026-08-17**. The board was celebrating a
       card he won two days earlier.

       ⛔ **THE WINDOW ABOVE IS RIGHT AND THE COMMENT ON IT SAYS SO** - *"without a window the victory
       screen would re-celebrate a card won yesterday every time you win anything"* - and then the
       fallback did precisely that whenever nothing was fresh, which is exactly the case it was
       guarding: a match that paid nothing. **A guard with an else-branch that does the forbidden
       thing is not a guard.**

       ⭐ Nothing fresh means nothing to celebrate. An unparseable `atUtc` also lands here, and that
       is correct: a receipt we cannot date is one we cannot claim was just earned. Silence is the
       honest failure; a false prize is not. */
    const shown = fresh;
    const paid = shown.filter((a) => a.cards > 0 || a.experience > 0 || a.scrip > 0);

    // 🚨 **A PLAYER WHO IS NOT ELIGIBLE MUST BE TOLD, OR THE WHOLE COLLECTION IS INVISIBLE TO THEM.**
    //
    // Access to play is fully open; only the Longhunter DROP requires a linked wallet (Alex: *"only
    // people who connect their wallet and we have the actual profile are eligible"*). So a new beta
    // tester wins their first match, is paid nothing, and has no way to learn that a limited
    // collection existed and they just failed to qualify for a copy of it.
    //
    // ⭐ Alex has flagged this exact failure before, about the beta grant: *"we did not tell them?
    // that is a huge loss of marketing."* A reward nobody knows about is not a reward, and the
    // moment they just won is the only moment they are guaranteed to care.
    const linked = await (async () => {
      try { return !!(await Api.userData())?.["primaryWalletAddress"]; } catch { return true; }
    })();
    if (!linked) {
      const head = document.createElement("p");
      head.className = "endcard__prizehead";
      head.textContent = "CONNECT A WALLET TO WIN CARDS";

      const why = document.createElement("p");
      why.className = "endcard__prizewhat";
      // 🚨 **THIS COPY MUST NOT IMPLY THAT PAST MATCHES ARE RECOVERABLE.** It used to, and the
      // server used to try: wins were banked while unlinked and settled on link. Alex, 2026-08-17:
      // *"Retroactive link doesn't fix the problem because the supply for one of the collections is
      // race to win not race to mint."* A Longhunter copy is spent at the moment it is awarded, so a
      // card promised to an unlinked player may simply not exist by the time they connect. Telling
      // them their wins are saved and then paying nothing is worse than telling them the rule now.
      why.textContent = "Longhunter cards are won, not bought, and each one is gone once the last "
                      + "copy is claimed. They can only be awarded to a connected wallet, so connect "
                      + "first and every match after that can pay one.";

      // ⭐ A BUTTON, NOT A SIGNPOST. Alex, 2026-08-16: *"an unlinked player should get prompted to
      // connect a wallet to get their reward."* The first version of this pointed at the Profile
      // screen, which is a sentence telling the player to go and find the feature themselves - the
      // same defect as the Tent link before it became clickable.
      const go = document.createElement("button");
      go.type = "button";
      go.className = "endcard__mintlink";
      go.textContent = "CONNECT WALLET AND CLAIM";

      const said = document.createElement("p");
      said.className = "endcard__prizenote";

      go.addEventListener("click", () => {
        void (async () => {
          go.disabled = true;
          try {
            const { linkWalletFlow } = await import("../net/linkWallet");
            const r = await linkWalletFlow();
            if (!r.ok) { said.textContent = r.reason; return; }
            // ⚠️ NOTHING IS CLAIMED HERE, and there is deliberately no route that would. Linking
            // starts eligibility; it does not reach backwards. See the note above.
            said.textContent = "Wallet connected. Your next match can pay a Longhunter card.";
            head.textContent = "CONNECTED";
            go.remove();
          } finally {
            go.disabled = false;
          }
        })();
      });

      host.append(head, why, go, said);
      host.hidden = false;
    }

    // ⚠️ Returns AFTER the not-eligible notice, never before it: a losing-nothing player is exactly
    // the one who needs to be told, and an early return here is what made this invisible.
    if (!paid.length) return;

    // ⚠️ Awaited: `cardName` reads a module-level index populated asynchronously, and asking before
    // it resolves returns undefined for every id - which would print "You won: , " and look broken.
    await loadCardNames();

    // ⭐ THE LONGHUNTER DROP GETS ITS OWN WORDS. Alex, 2026-08-16: *"the pop up you have built is for
    // campaign wins. this new pop up is for beta win"*, and then chose the name: *"lets call it
    // Longhunter."* It reads differently on purpose - a campaign card is always available to win,
    // a Longhunter card is gone the moment the last copy is claimed, and a player who cannot tell
    // the two apart has no reason to value the scarce one.
    //
    // ⭐ KEYED ON THE REASON PREFIX THE SERVER OWNS (`ProgressionStore.EarlyAdopterReason`), never on
    // the card, the rarity or the count. Those are all properties a campaign award can share.
    const isLonghunter = (r: string) => (r || "").startsWith("longhunter:");
    let wonACard = false;

    for (const a of paid) {
      const head = document.createElement("p");
      head.className = "endcard__prizehead";
      head.textContent = isLonghunter(a.reason) ? "LONGHUNTER FIND" : "YOU WON";

      const bits: string[] = [];
      const names = (a.cardIds || []).map((id) => cardName(id)).filter(Boolean);
      if (names.length) bits.push(names.join(", "));
      else if (a.cards > 0) bits.push(`${a.cards} card${a.cards === 1 ? "" : "s"}`);
      if (a.experience > 0) bits.push(`${a.experience} RXP`);
      // ⭐ Scrip was being paid all along and never mentioned - see Rewards.cs for the stale comment
      // that hid it. ⛔ NOT "doubloons": that is the Saloon's currency, not ours.
      if (a.scrip > 0) bits.push(`${a.scrip} scrip`);

      const what = document.createElement("p");
      what.className = "endcard__prizewhat";
      what.textContent = bits.join("  ·  ");

      host.append(head, what);

      if (isLonghunter(a.reason) && (names.length || a.cards > 0)) {
        const why = document.createElement("p");
        why.className = "endcard__prizenote";
        // ⚠️ No number here on purpose. Alex: *"dont say only 4000 will ever exist as that looks
        // like a big number."* The scarcity is stated as a rule, not as a total.
        why.textContent = "A beta find. Only so many of this card will ever be won, and when they "
                        + "are gone they are gone.";
        host.append(why);
      }
      if (names.length || a.cards > 0) wonACard = true;
    }


    // ⭐ Only mention the mint when a CARD was won. RXP is not mintable, and offering to mint
    // experience would be a promise the chain cannot keep.
    //
    // ⚠️ IT IS A LINK, NOT A SENTENCE. Alex, 2026-08-16: *"when someone wins a card in revival is
    // there a call to action to invite them to the tent to mint it?"* There was a line of text
    // saying the Tent exists, and nothing to click. Telling a player where to go at the exact moment
    // they have won something, then making them find it themselves, wastes the only moment they are
    // guaranteed to care.
    if (wonACard) {
      const mint = document.createElement("p");
      mint.className = "endcard__prizenote";
      mint.textContent = "It is yours to keep. ";

      const go = document.createElement("a");
      go.className = "endcard__mintlink";
      go.href = TENT;
      go.target = "_blank";
      go.rel = "noopener noreferrer";
      go.textContent = "Mint it at the Revival Tent";
      mint.append(go);

      host.append(mint);
    }
    host.hidden = false;
  } catch {
    // Silent by design: see the doc comment. The player keeps the cards either way.
  }
}

export function endOverlay(
  won: boolean,
  words: string,
  onAgain: () => void,
  onLeave: () => void,
): HTMLElement {
  const root = document.createElement("div");
  root.className = `endcard endcard--${won ? "won" : "lost"}`;
  root.setAttribute("role", "dialog");
  root.dataset["result"] = won ? "VICTORY" : "DEFEAT";

  const card = document.createElement("div");
  card.className = "endcard__card";

  const title = document.createElement("h2");
  title.className = "endcard__title";
  title.textContent = won ? "VICTORY" : "DEFEAT";

  const line = document.createElement("p");
  line.className = "endcard__line";
  line.textContent = words;

  const row = document.createElement("div");
  row.className = "endcard__row";
  row.append(
    endPlate("PLAY AGAIN", onAgain),
    endPlate("LEAVE", onLeave, true),
  );

  // ⭐ **WHAT YOU JUST WON, ON THE SCREEN THAT SAYS YOU WON.**
  //
  // 🚨 Alex, 2026-08-15: *"I have won 2 campaigns already and have no idea what I won and it is a
  // huge loss and poor UX as is, we want to retain players, they need to win and know what they
  // win."* The award was paid into his documents and the victory card said "VICTORY" and offered
  // PLAY AGAIN or LEAVE. **A prize nobody is told about is not a prize.**
  //
  // ⚠️ Filled ASYNCHRONOUSLY and only when there is something to say. The overlay must appear the
  // instant the match ends - blocking it on a fetch would trade a real bug for a worse one, and a
  // "you won nothing" line on a duel or a practice match is noise.
  const prize = document.createElement("div");
  prize.className = "endcard__prize";
  prize.hidden = true;
  card.append(title, line, prize, row);
  root.append(card);

  // ⭐ **SHOWN ON A DEFEAT TOO, AND THAT IS THE POINT OF THE CHANGE.** Alex, 2026-08-16: *"first game
  // win or lose get a card."* The server now pays the very first match regardless of outcome, and a
  // reward a losing player never SEES is the same as no reward - which is the funnel problem this is
  // fixing, not a smaller version of it.
  //
  // ⚠️ `showPrize` already returns silently when there is nothing from the last 90 seconds, so a
  // normal loss still renders exactly as before. Nothing here decides WHETHER a card was earned; the
  // server does, and this only displays what the ledger says.
  void showPrize(prize);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      window.removeEventListener("keydown", onKey);
      onLeave();
    }
  };
  window.addEventListener("keydown", onKey);

  return root;
}

function endPlate(label: string, onClick: () => void, quiet = false): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = quiet ? "plate plate--quiet" : "plate";
  b.dataset["label"] = label;
  const face = document.createElement("span");
  face.className = "plate__face";
  face.setAttribute("aria-hidden", "true");
  const l = document.createElement("span");
  l.className = "plate__label";
  l.textContent = label;
  b.append(face, l);
  b.addEventListener("click", onClick);
  return b;
}


/**
 * THE HERO POWER.
 *
 * ⭐ **EVERY HERO HAS ONE AND NONE OF THEM WERE REACHABLE.** The data has carried
 * `heroPowerName`, `heroPowerDescription`, `heroPowerCost` and an `effectsIds` entry all along -
 * "Restorative Touch", 2 foxfire, restore 2 Health. Ten of our sound effects are waived on this
 * feature not existing.
 *
 * ⭐ **THE ORIGINAL'S OWN CONDITIONS**, from `GetAvailableAbility` (Revival.Client :18854): it is
 * usable only when it is YOUR turn, on YOUR hero, when the ability still has charges, when you are
 * not already picking a target, when the hero is not Stunned and the timer is not paused. Firing it
 * runs the `AbilityButtonPress` phase.
 *
 * ⚠️ **THE COST IS SHOWN ON THE BUTTON, NOT ONLY ENFORCED.** A control that greys out without
 * saying what it wants teaches nothing; the number is why it is grey.
 */
export function heroPowerEl(
  name: string,
  cost: number,
  description: string,
  enabled: boolean,
  why: string,
  onUse: () => void,
): HTMLElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "heropower";
  b.dataset["label"] = name || "HERO POWER";
  b.disabled = !enabled;
  b.classList.toggle("heropower--ready", enabled);
  // ⚠️ The tooltip carries the RULES TEXT, so a player can read what it does before spending on it.
  b.title = description ? `${description}${enabled ? "" : ` — ${why}`}` : why;

  const c = document.createElement("span");
  c.className = "heropower__cost";
  c.textContent = String(cost);

  const n = document.createElement("span");
  n.className = "heropower__name";
  n.textContent = name || "HERO POWER";

  b.append(c, n);
  b.addEventListener("click", onUse);
  return b;
}
